import { StackScreenProps } from "@react-navigation/stack";
import { SetupRootParamList } from "../../../../types";
import {
  Button,
  FlatActionList,
  FlatActionListItem,
  Page,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { useSetupContextProvider } from "../SetupScreen/hooks";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  MUTATION_SWITCH_ACTIVE_TERMINAL,
  QUERY_TERMINAL_SESSION_DETAILS,
} from "../../../../utilities/auth/constants";
import {
  CurrentUserV2,
  TerminalSessionDetail,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import Terminal from "../../../../utilities/data/model/Terminal";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { MUTATION_UPDATE_TERMINAL } from "../../../settings/common";
import colors from "react-native-ui-lib/src/style/colors";
import { Feather } from "@expo/vector-icons";
import { View } from "react-native-ui-lib";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";

export type SetupSelectTerminalScreenProps = StackScreenProps<
  SetupRootParamList,
  "SetupSelectTerminalScreen"
>;

export const SetupSelectTerminalScreen = ({
  navigation,
}: SetupSelectTerminalScreenProps) => {
  const [selectedTerminalId, setSelectedTerminalId] = React.useState<
    string | null
  >(null);

  const [sessionDetails, setSessionDetails] = React.useState<
    TerminalSessionDetail[] | undefined
  >(undefined);

  const database = useDatabase();
  const transitionEnded = useHasTransitionEnded(navigation);

  const [availableTerminals, setAvailableTerminals] = React.useState<
    Terminal[] | undefined
  >(undefined);

  const { appState, updateAppState } = useWithAppState();

  const { setBootStrapped } = useSetupContextProvider();

  const [queryTerminalSessionDetails] = useLazyQuery<{
    terminalSessionDetails: TerminalSessionDetail[];
  }>(QUERY_TERMINAL_SESSION_DETAILS);

  const [mutationUpdateTerminal] = useMutation<{ updateTerminal: Terminal }>(
    MUTATION_UPDATE_TERMINAL,
  );

  const [mutationSetActiveTerminal] = useMutation<{
    switchActiveTerminal: CurrentUserV2;
  }>(MUTATION_SWITCH_ACTIVE_TERMINAL);

  React.useEffect(() => {
    database
      .get<Terminal>("terminals")
      .query()
      .fetch()
      .then((i) => setAvailableTerminals(i));
  }, []);

  React.useEffect(() => {
    queryTerminalSessionDetails()
      .then((res) => {
        setSessionDetails(res.data?.terminalSessionDetails ?? []);
      })
      .catch((err) => {
        console.error("error fetching terminal session details", err);
      });
  }, []);

  const handleOnSubmit = async () => {
    if (!selectedTerminalId) {
      return;
    }
    try {
      const result = await mutationSetActiveTerminal({
        variables: {
          terminalId: selectedTerminalId,
        },
      });

      if (!result.data?.switchActiveTerminal) {
        throw new Error("Failed to enable terminal");
      }

      updateAppState({
        terminalId: selectedTerminalId,
      });

      setBootStrapped();
    } catch (error) {
      console.error("Error enabling terminal:", error);
    }
  };

  const numberOfEnabledTerminals = availableTerminals?.filter(
    (t) => t.enabled,
  ).length;

  const noTerminals =
    availableTerminals === undefined || numberOfEnabledTerminals === 0;

  const title = noTerminals
    ? "No terminals available"
    : numberOfEnabledTerminals === 1
    ? "Select your terminal"
    : `Select from ${numberOfEnabledTerminals} available terminals`;

  const secondaryTitle = noTerminals
    ? "Please check your subscription or contact support for assistance."
    : "You have multiple terminals available. Please select the terminal you want to use for this session.";

  const items: FlatActionListItem[] = (
    availableTerminals?.map((terminal) => {
      const enabled = terminal.enabled;
      const disabled = !terminal.enabled;
      const isActive = selectedTerminalId === terminal.id;

      const warningText =
        enabled === false && isActive
          ? "Required to switch from disabled terminal!"
          : undefined;

      const matchingSessionDetail = sessionDetails?.find(
        (detail) => detail.terminalId === terminal.id,
      );

      const sessionsText = matchingSessionDetail
        ? `${matchingSessionDetail.numberOfActiveSessions} active session${
            matchingSessionDetail.numberOfActiveSessions > 1 ? "s" : ""
          }`
        : undefined;

      return {
        key: terminal.id,
        title: terminal.name,
        subTitle: warningText || sessionsText,
        onPress: () => setSelectedTerminalId(terminal.id),
        selected: isActive,
        disabled: disabled,
        componentRight: (icon: any) => {
          return isActive ? (
            <Feather name="check" size={24} color={colors.action} />
          ) : (
            <View />
          );
        },
      };
    }) || []
  ).filter((f) => f.disabled !== true);

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={!selectedTerminalId}
          label={"Continue"}
          isAction
          onPress={() => handleOnSubmit()}
        ></Button>
      </View>
    </View>
  );

  if (
    availableTerminals === undefined ||
    sessionDetails === undefined ||
    !transitionEnded
  ) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  return (
    <Page title={title} secondaryTitle={secondaryTitle} footer={footerMarkup}>
      <FlatActionList items={items} />
    </Page>
  );
};
